/**
 * Lightweight return-to-list memory for the PDP back button.
 *
 * When the user clicks a product card we remember WHERE they came from
 * (path + query) and HOW FAR they had scrolled. From the PDP we expose
 * a back button that returns to that exact spot, and the list page
 * restores the scroll position once the products it streams in are tall
 * enough to honour the requested offset.
 *
 * Stored in sessionStorage so it survives a hard navigation but never
 * leaks across tabs / sessions.
 */

const KEY = "pdp:return";

export interface ReturnEntry {
  url: string; // pathname + search
  scrollY: number;
  ts: number; // ms epoch — used to expire stale entries (>30min)
}

const MAX_AGE_MS = 30 * 60 * 1000;

function readRaw(): ReturnEntry | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ReturnEntry;
    if (!parsed || typeof parsed.url !== "string") return null;
    if (Date.now() - (parsed.ts ?? 0) > MAX_AGE_MS) {
      sessionStorage.removeItem(KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/** Called from a product card just before navigation to the PDP. */
export function rememberReturn(url: string, scrollY: number): void {
  try {
    sessionStorage.setItem(
      KEY,
      JSON.stringify({ url, scrollY, ts: Date.now() } satisfies ReturnEntry),
    );
  } catch {
    /* sessionStorage may be unavailable (Safari private). Silently skip. */
  }
}

/** Peek without consuming — used by PDP to decide if the back button shows. */
export function peekReturn(): ReturnEntry | null {
  return readRaw();
}

/**
 * Atomically consume the entry IF its url matches the current page.
 * Used by the list page to restore scroll exactly once.
 */
export function consumeReturnFor(url: string): number | null {
  const entry = readRaw();
  if (!entry || entry.url !== url) return null;
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* noop */
  }
  return entry.scrollY;
}
